import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, Star, Users } from 'lucide-react';
import ShareButton from './ShareButton';

// Moved statistics array OUTSIDE the component
const statistics = [
  {
    icon: Users,
    value: '15,000+',
    label: 'Osób zmieniło karierę',
    color: 'text-blue-600'
  },
  {
    icon: TrendingUp,
    value: '78%',
    label: 'Średni wzrost zarobków',
    color: 'text-green-600'
  },
  {
    icon: Star,
    value: '95%',
    label: 'Poziom satysfakcji użytkowników',
    color: 'text-yellow-600'
  }
];

const testimonials = [
  {
    name: 'Julia Sułkowska',
    role: 'Studentka psychologii → Oddział psychologii SA',
    image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=60', // Reduced quality for testimonial images
    quote: 'Dzięki wam jeszcze na studiach odkryłam, że moje umiejętności zespołowe i analityczne świetnie sprawdzają się w zarządzaniu w centrum psychologicznym.',
    timeToTransition: '1 miesiąc',
    salaryIncrease: '100%'
  },
  {
    name: 'Marek Nowicki',
    role: 'Nauczyciel → UX Designer',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=60', // Reduced quality for testimonial images
    quote: 'Zawsze lubiłem tworzyć sztukę, ale myślałem że to tylko hobby. Kariera z AI pokazała mi, jak przekuć pasję w dochodową karierę, obecnie designuje strony internetowe.',
    timeToTransition: '2 miesięce',
    salaryIncrease: '72%'
  },
  {
    name: 'Anna Wiśniewska',
    role: 'Sprzedawca → Digital Marketing Manager',
    image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=60', // Reduced quality for testimonial images
    quote: 'Z pomocą AI odkryłam, że moje umiejętności sprzedażowe świetnie sprawdzają się w marketingu cyfrowym. Teraz zarabiam więcej i pracuję zdalnie!',
    timeToTransition: '3 miesięcy',
    salaryIncrease: '55%'
  }
];

export default function SuccessStories() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 10000);

    return () => clearInterval(timer);
  }, []);

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  return (
    <section className="py-7 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Historie Sukcesu
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Zobacz, jak inni zmienili swoją karierę i osiągnęli sukces z pomocą sztucznej inteligencji
          </p>
        </div>

        {/* Statystyki */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-7 mb-6">
          {statistics.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center flex flex-col items-center justify-center"
            >
              <div className="flex items-center justify-center mb-4">
                <stat.icon className={`w-8 h-8 ${stat.color}`} />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">
                {stat.value}
              </div>
              <div className="text-sm text-gray-600">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Testimonials Carousel */}
        <div className="relative h-[400px] md:h-[300px] max-w-4xl mx-auto overflow-hidden">
          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }}
              className="absolute w-full"
            >
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <div className="flex flex-col md:flex-row items-center md:items-start gap-4 md:gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-full overflow-hidden">
                      <img
                        src={`${testimonials[currentIndex].image}`} // Reduced quality URLs already in testimonial data
                        alt={testimonials[currentIndex].name}
                        className="w-full h-full object-cover"
                        loading="eager" // Eager loading for testimonial images
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-col md:flex-row items-center md:items-start justify-between mb-4 text-center md:text-left">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {testimonials[currentIndex].name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-2 md:mb-0">
                          {testimonials[currentIndex].role}
                        </p>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1">
                          <TrendingUp className="w-4 h-4 text-green-500" />
                          <span className="font-medium text-green-700">
                            +{testimonials[currentIndex].salaryIncrease} zarobki
                          </span>
                        </div>
                      </div>
                    </div>
                    <blockquote className="text-gray-700 text-lg italic mb-6 text-center md:text-left">
                      "{testimonials[currentIndex].quote}"
                    </blockquote>
                    <div className="text-sm text-gray-500 text-center md:text-left">
                      Czas transformacji: {testimonials[currentIndex].timeToTransition}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Dots */}
          <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setDirection(index > currentIndex ? 1 : -1);
                  setCurrentIndex(index);
                }}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentIndex ? 'bg-purple-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="flex justify-center mt-4">
        <ShareButton
          title="Historie sukcesu w Kariera z AI"
          text="Zobacz, jak inni zmienili swoją karierę z pomocą AI!"
        />
      </div>
      <div className="mt-4"></div>
      <div className="mt-4"></div> {/* Zmniejszony odstęp */}
    </section>
  );
}
